import { ChevronDownIcon, SearchIcon } from '@chakra-ui/icons';
import {
    Box, Button, Checkbox, Flex, HStack, Input, InputGroup, InputRightElement, List, ListItem, Menu, MenuButton, MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner, Text, useColorModeValue, VStack
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useEffect, useState } from 'react';
import { MdEmail } from 'react-icons/md';
import { toast } from "react-toastify";
import { sendCommunications } from 'services/CommunicationService';
import { getAllNotPaginated } from 'services/CustomersService';
import { getAllPaginated } from 'services/ModelService';
import { getAll } from 'services/ModelTypeService';
import { Customer, Filters, GetModelPaginatedRs, GetModelTypeRs, Model } from 'utils/interfaces';

const SendCommunicationsModal = (
    {
        selectedModel,
        setSelectedModel,
        isOpen,
        onClose
    }: {
        selectedModel: Model | null;
        setSelectedModel: (model: Model | null) => void;
        isOpen: boolean;
        onClose: () => void
    }
) => {

    const { keycloak } = useKeycloak();

    const [modelTypes, setModelTypes] = useState<GetModelTypeRs[]>([]);
    const [models, setModels] = useState<GetModelPaginatedRs>();

    const [selectedModelType, setSelectedModelType] = useState<GetModelTypeRs | null>(null);
    const [recipients, setRecipients] = useState<string[]>([]); // Array di email

    const [subject, setSubject] = useState<string>("");
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]); // Stato per i file selezionati

    const [modelLoading, setModelLoading] = useState<boolean>(false);
    const [sendCommunicationLoading, setSendCommunicationLoading] = useState<boolean>(false);

    // Customer SearchBox
    const [searchedCustomer, setSearchedCustomer] = useState('');
    const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]); // Array di clienti selezionati    
    const [customers, setCustomers] = useState<Customer[]>(); // Correctly initialized as an array
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 30; // Numero di clienti da mostrare per pagina

    const paginatedCustomers = customers?.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    useEffect(() => {
        const initializeModalData = async () => {
            setSelectedModel(selectedModel);
            setSelectedModelType(selectedModel?.modelType);

            try {
                // Ottieni i tipi di modello
                const modelTypes = await getAll(keycloak.token, onSessionExpired);
                setModelTypes(modelTypes);

                // Usa direttamente il modelTypeId del modello selezionato
                const modelTypeId = selectedModel?.modelType?.id;
                if (modelTypeId) {
                    await fetchModels({ modelTypeId });
                }
            } catch (error) {
                console.error("Errore durante l'inizializzazione dei dati della modale:", error);
                toast.error("Errore durante il caricamento dei dati.", {
                    position: "bottom-right",
                });
            }
        };

        if (isOpen) {
            initializeModalData();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            setSelectedFiles((prevFiles) => [...prevFiles, ...Array.from(files)]); // Aggiungi i nuovi file a quelli esistenti
        }
    };

    const onSessionExpired = () => {
        toast.error("Sessione scaduta. Effettua nuovamente l'accesso.", {
            position: "bottom-right",
            onClose: () => {
                keycloak.logout();
                localStorage.removeItem("kc-token");
            }
        });
    };

    const handleModelTypeClick = async (modelType: GetModelTypeRs) => {
        setSelectedModelType(modelType);
        await fetchModels({ modelTypeId: modelType.id });
    };

    const shadow = useColorModeValue(
        '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
        '14px 17px 40px 4px rgba(112, 144, 176, 0.06)'
    );

    const fetchModels = async (filters: any) => {
        setModelLoading(true);
        try {
            const modelsData = await getAllPaginated(
                keycloak.token,
                onSessionExpired,
                filters
            );

            setModels(modelsData);
        } catch (err) {
            console.error(err);
            toast.error(
                "Errore durante l'acquisizione dei modelli, perfavore riprova.", {
                position: "bottom-right",
            });
        } finally {
            setModelLoading(false);
        }
    };

    const resetState = () => {
        setSelectedCustomers([]);
        setSearchedCustomer("");
        setSelectedModelType(null);
        setSelectedModel(null);
        setModels(undefined);
        setRecipients([]);
        setSubject("");
        setSelectedFiles([]);
    };

    const sendCommunicationsOnBackend = async () => {
        setSendCommunicationLoading(true);
        try {
            const communicationsResponse = await sendCommunications(
                keycloak.token,
                onSessionExpired,
                [], // Le comunicazioni vanno inviate solo in base ai clienti selezionati
                subject,
                selectedModelType?.id,
                selectedModel?.id,
                selectedCustomers.map((customer) => customer.id), // Estrai solo gli ID dei clienti
                selectedFiles
            );

            if (!communicationsResponse) {
                return;
            }

            toast.success(
                "Comunicazione inviata con successo", {
                position: "bottom-right",
            });

            // Ritarda la chiusura della modale per permettere al toast di essere visualizzato
            setTimeout(() => {
                resetState();
                onClose();
            }, 50);

        } catch (err) {
            console.error(err);
            toast.error(
                "Errore durante l'invio della comunicazione, perfavore riprova.", {
                position: "bottom-right",
            });
        } finally {
            setSendCommunicationLoading(false);
        }
    }

    const fetchCustomers = async (fullName?: string) => {
        try {
            const filters: Filters<string> = {
                fullName
            };

            const customers: Customer[] = await getAllNotPaginated(
                keycloak.token,
                onSessionExpired,
                filters
            );

            setCustomers(customers);

        } catch (error) {
            toast.error(
                "Errore durante il reperimento della lista clienti", {
                position: "bottom-right",
            });
        }
    };

    const handleCustomerSelect = (customer: Customer) => {
        setSelectedCustomers((prev) => {
            if (prev?.some((c) => c.id === customer.id)) {
                // Rimuovi il cliente se è già selezionato
                setRecipients((recipients) =>
                    recipients.filter((email) => email.toLowerCase() !== customer.email?.trim().toLowerCase())
                ); // Rimuovi l'email dal campo recipients
                return prev.filter((c) => c.id !== customer.id);
            } else {
                // Aggiungi il cliente se non è già selezionato
                setRecipients((recipients) => {
                    const normalizedEmail = customer.email?.trim().toLowerCase();
                    if (
                        normalizedEmail && // L'email deve essere valida
                        !recipients.map((email) => email.toLowerCase()).includes(normalizedEmail) // Controlla che l'email non sia già presente
                    ) {
                        return [...recipients, normalizedEmail];
                    }
                    return recipients;
                });
                return [...prev, customer];
            }
        });
    };

    return (
        <Modal isOpen={isOpen}
            onClose={() => {
                resetState();
                onClose();
            }}
            size="6xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader backgroundColor="white" color="#9FB50C" zIndex={1} textAlign="left">
                    Nuova Comunicazione
                </ModalHeader>
                <ModalCloseButton _focus={{ boxShadow: 'none' }} zIndex={10} />
                <ModalBody>
                    <HStack
                        alignItems="flex-start"
                        spacing={4}
                        flexDirection={{ base: "column", md: "row" }} // Colonne verticali su dispositivi mobili, orizzontali su desktop
                        width="100%"
                    >                             {/* Colonna sinistra con i tipi di modelli */}
                        <VStack alignItems="flex-start" spacing={4} width={{ base: "100%", md: "30%" }}>
                            <Box
                                backgroundColor="white"
                                borderRadius="20px"
                                boxShadow={shadow}
                                width="100%"
                                p={4}
                                height="300px" // Imposta un'altezza fissa
                                overflowY="auto" // Rendi la lista scrollabile
                                css={{
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#688CC8',
                                        borderRadius: '24px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: '#688CC8',
                                    },
                                }}
                            >
                                <Text fontWeight="bold" mb={4}>Tipo Modello: <Text as="span" color="red">*</Text></Text>
                                <List spacing={3}>
                                    {modelTypes?.map((modelType) => (
                                        <ListItem
                                            key={modelType.id}
                                            cursor="pointer"
                                            onClick={() => handleModelTypeClick(modelType)}
                                            bg={selectedModelType?.id === modelType.id ? "gray.300" : "white"}
                                            p={2}
                                            borderRadius="md"
                                        >
                                            {modelType.type}
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                            <Box
                                backgroundColor="white"
                                borderRadius="20px"
                                boxShadow={shadow}
                                width="100%"
                                p={4}
                                height="300px" // Imposta un'altezza fissa
                                overflowY="auto"
                                css={{
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#688CC8',
                                        borderRadius: '24px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: '#688CC8',
                                    },
                                }}
                            >
                                <Text fontWeight="bold" mb={2}>Modello: <Text as="span" color="red">*</Text></Text>
                                {modelLoading ? (
                                    <Flex justifyContent='center' alignItems='center' height='50%'>
                                        <Spinner size='xl' color='#9FB50C' />
                                    </Flex>
                                ) : (
                                    <List spacing={3}>
                                        {models?.content.map((model) => (
                                            <ListItem
                                                key={model.id}
                                                cursor="pointer"
                                                onClick={() => {
                                                    setSelectedModel(model)
                                                }} // Imposta il modello selezionato
                                                bg={selectedModel?.id === model.id ? "gray.300" : "white"} // Cambia lo sfondo se selezionato
                                                p={2}
                                                borderRadius="md"
                                            >
                                                {model.name}
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </Box>
                        </VStack>

                        {/* Sezioni a destra */}
                        <VStack alignItems="flex-start" spacing={4} width={{ md: "70%" }}>
                            <Box
                                backgroundColor="white"
                                borderRadius="20px"
                                boxShadow={shadow}
                                width="100%"
                                p={4}
                            >
                                <Box mb={4}>
                                    <Text fontWeight="bold" mb={2}>
                                        Clienti: <Text as="span" color="red">*</Text>
                                    </Text>
                                    <Menu placement="bottom" flip={false} closeOnSelect={false}> {/* Impedisce la chiusura del menu */}
                                        <MenuButton
                                            borderRadius="6px"
                                            as={Button}
                                            rightIcon={<ChevronDownIcon />}
                                            bg="gray.200"
                                            onClick={() => {
                                                fetchCustomers();
                                            }}
                                        >
                                            {selectedCustomers?.length > 1
                                                ? `${selectedCustomers?.length} clienti selezionati`
                                                : selectedCustomers?.length === 1
                                                    ? "1 Cliente selezionato"
                                                    : "Seleziona Clienti"}
                                        </MenuButton>
                                        <MenuList maxHeight="250px" overflowY="auto" width="350px" mt={1}>
                                            {/* Searchbox */}
                                            <Box p="2">
                                                <InputGroup>
                                                    <Input
                                                        placeholder="Cerca per ragione sociale..."
                                                        value={searchedCustomer}
                                                        onChange={(e) => setSearchedCustomer(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                fetchCustomers(searchedCustomer); // Trigger search on Enter
                                                            }
                                                        }}
                                                    />
                                                    <InputRightElement width="3rem">
                                                        <Button
                                                            h="1.75rem"
                                                            size="sm"
                                                            onClick={() => {
                                                                setCurrentPage(1); // Resetta la pagina corrente
                                                                fetchCustomers(searchedCustomer);
                                                            }}                                                        >
                                                            <SearchIcon />
                                                        </Button>
                                                    </InputRightElement>
                                                </InputGroup>
                                            </Box>

                                            {/* Opzione "Tutti" */}
                                            <MenuItem
                                                bg={selectedCustomers?.length === customers?.length ? "gray.300" : "white"}
                                            >
                                                <Checkbox
                                                    isChecked={selectedCustomers?.length === customers?.length}
                                                    mr={2}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            // Seleziona tutti
                                                            const allEmails = customers
                                                                ?.map((customer) => customer.email?.trim().toLowerCase())
                                                                .filter((email) => email && email !== "")
                                                                .filter((email, index, self) => self.indexOf(email) === index) || [];

                                                            setSelectedCustomers(customers || []);
                                                            setRecipients(allEmails);
                                                        } else {
                                                            // Deseleziona tutti
                                                            setSelectedCustomers([]);
                                                            setRecipients([]);
                                                        }
                                                    }}
                                                />
                                                Tutti
                                            </MenuItem>

                                            {/* Lista clienti */}
                                            {paginatedCustomers?.map((customer: Customer) => (
                                                <MenuItem
                                                    key={customer.id}
                                                    bg={selectedCustomers?.some((c) => c.id === customer.id) ? "gray.300" : "white"}
                                                >
                                                    <Checkbox
                                                        isChecked={selectedCustomers?.some((c) => c.id === customer.id)}
                                                        mr={2}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                // Aggiungi il cliente
                                                                handleCustomerSelect(customer);
                                                            } else {
                                                                // Rimuovi il cliente
                                                                handleCustomerSelect(customer);
                                                            }
                                                        }}
                                                    />
                                                    {customer.fullName}
                                                </MenuItem>
                                            ))}
                                            {/* Navigazione tra le pagine */}
                                            <Flex justifyContent="space-between" p={2}>
                                                <Button
                                                    size="sm"
                                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                    isDisabled={currentPage === 1}
                                                >
                                                    Precedente
                                                </Button>
                                                <Text fontSize="sm">
                                                    Pagina {currentPage} di {Math.ceil((customers?.length || 1) / itemsPerPage)}
                                                </Text>
                                                <Button
                                                    size="sm"
                                                    onClick={() => setCurrentPage((prev) => prev + 1)}
                                                    isDisabled={currentPage * itemsPerPage >= customers?.length}
                                                >
                                                    Successiva
                                                </Button>
                                            </Flex>
                                        </MenuList>
                                    </Menu>
                                </Box>
                                <Box mb={6}>
                                    <Box
                                        border="1px solid"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        p={2}
                                        minHeight="50px"
                                        maxHeight="150px" // Altezza massima per il contenitore
                                        overflowY="auto" // Abilita lo scroll verticale
                                        display="flex"
                                        flexWrap="wrap"
                                        alignItems="center"
                                    >
                                        {recipients.length === 0 ? (
                                            <Text color="gray.400">
                                                Email che riceveranno la comunicazione
                                            </Text>
                                        ) : (
                                            recipients.map((email, index) => (
                                                <Flex
                                                    key={index}
                                                    alignItems="center"
                                                    bg="gray.200"
                                                    borderRadius="md"
                                                    p={1}
                                                    m={1}
                                                >
                                                    <Text mr={2}>{email}</Text>
                                                </Flex>
                                            ))
                                        )}
                                    </Box>
                                </Box>
                                <Box mt={4}>
                                    <Text fontWeight="bold" mb={2}>Oggetto: <Text as="span" color="red">*</Text></Text>
                                    <Input
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        placeholder="Inserisci l'oggetto"
                                    />
                                </Box>
                                <Box mt={4}>
                                    <Text fontWeight="bold" mb={2}>
                                        Allegati:
                                    </Text>
                                    <Box
                                        p={4}
                                        border="1.5px dashed gray" // Bordi tratteggiati
                                        borderRadius="md"
                                        textAlign="center"
                                        onDragOver={(e) => e.preventDefault()} // Previeni il comportamento predefinito del drag
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            const files = e.dataTransfer.files;
                                            if (files) {
                                                setSelectedFiles((prevFiles) => [...prevFiles, ...Array.from(files)]); // Aggiungi i file trascinati allo stato
                                            }
                                        }}
                                    >
                                        <Text color="gray.600" fontWeight={"bold"}>Trascina qui i file da allegare</Text>
                                        <Text color="gray.400">Dimensione massima consentita: 50MB</Text>
                                        <Input
                                            id="file-upload"
                                            type="file"
                                            multiple
                                            display="none" // Nascondi l'input
                                            onChange={handleFileChange} // Gestisci il cambiamento dei file
                                            isDisabled={sendCommunicationLoading}
                                        />
                                        <Button
                                            as="label"
                                            htmlFor="file-upload"
                                            mt={2}
                                            colorScheme="blue"
                                            backgroundColor="#688CC8"
                                            color="white"
                                            cursor="pointer"
                                            _hover={{ backgroundColor: "#5677A3" }}
                                            isDisabled={sendCommunicationLoading}
                                        >
                                            Oppure Carica
                                        </Button>
                                    </Box>

                                    {/* Lista dei file selezionati */}
                                    {selectedFiles.length > 0 && (
                                        <Box mt={4}>
                                            <Text textColor={"gray.400"} fontSize="sm" mb={2}>
                                                File selezionati:
                                            </Text>
                                            {selectedFiles.map((file, index) => (
                                                <Flex key={index} alignItems="center" mb={1}>
                                                    <Text as="span" mr={2}>
                                                        {file.name}
                                                    </Text>
                                                    <Button
                                                        size="xs"
                                                        colorScheme="red"
                                                        variant="ghost"
                                                        onClick={() => {
                                                            // Rimuovi il file selezionato dallo stato
                                                            setSelectedFiles((prevFiles) =>
                                                                prevFiles.filter((_, i) => i !== index)
                                                            );
                                                        }}
                                                    >
                                                        x
                                                    </Button>
                                                </Flex>
                                            ))}
                                        </Box>
                                    )}

                                </Box>
                            </Box>
                        </VStack>
                    </HStack>
                </ModalBody>
                <ModalFooter backgroundColor="white" zIndex={1}>
                    <Button
                        colorScheme={"#688CC8"}
                        backgroundColor={'#688CC8'}
                        color="white"
                        mr={3}
                        onClick={() => {
                            sendCommunicationsOnBackend();
                        }}
                        leftIcon={sendCommunicationLoading ? <Spinner size="sm" /> : <MdEmail />} // Mostra il loader se loading
                        isDisabled={!selectedModelType || !selectedModel || !subject || sendCommunicationLoading || !selectedCustomers} // Disabilita il pulsante se i campi non sono compilati
                    >
                        {sendCommunicationLoading ? "Invio..." : "Invia"} {/* Cambia il testo del pulsante */}
                    </Button>
                    <Button
                        textColor={"gray.900"}
                        colorScheme="gray"
                        onClick={() => {
                            resetState();
                            onClose();
                        }}
                    >
                        Annulla
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default SendCommunicationsModal;